import { sql } from 'drizzle-orm/sql/sql';
import { relations } from 'drizzle-orm/relations';
import { uuid, varchar, jsonb, boolean, timestamp, text } from 'drizzle-orm/pg-core/columns';
import { pgTable } from 'drizzle-orm/pg-core/table';
import { check } from 'drizzle-orm/pg-core/checks';
import type { Delta } from 'quill/core';
import { files } from './files';
import { commonFields, commonFieldsWithSoftDelete } from './common-fields';
import { companies } from './companies';
import { users } from './users';
import { FieldType } from './agreements.types';

export const templates = pgTable('agreement_templates', {
  ...commonFieldsWithSoftDelete,
  name: varchar({ length: 255 }).notNull(),
  language: varchar({ length: 2 }).$type<'sv' | 'en'>().notNull(),
  authorId: uuid()
    .references(() => users.id)
    .notNull(),
  defaultFolder: varchar({ length: 255 }),
});

export const companyTemplates = pgTable('company_templates', {
  createdAt: commonFields.createdAt,
  updatedAt: commonFields.updatedAt,
  companyId: uuid()
    .references(() => companies.id)
    .notNull(),
  templateId: uuid()
    .references(() => templates.id)
    .notNull(),
});

export const templateRevisions = pgTable('agreement_template_revisions', {
  ...commonFieldsWithSoftDelete,
  templateId: uuid()
    .references(() => templates.id)
    .notNull(),
  delta: jsonb().$type<Delta>().notNull(),
  deltaHash: varchar().generatedAlwaysAs(sql`encode(digest(delta::text, 'sha256'), 'hex')`),
  name: varchar().notNull(),
  description: varchar(),
  publishedAt: timestamp(),
  publishedBy: uuid().references(() => users.id),
});

export const templateRelations = relations(templates, ({ many, one }) => ({
  revisions: many(templateRevisions),
  author: one(users, {
    fields: [templates.authorId],
    references: [users.id],
  }),
  companyTemplates: many(companyTemplates),
}));

export const companyTemplateRelations = relations(companyTemplates, ({ one }) => ({
  company: one(companies, {
    fields: [companyTemplates.companyId],
    references: [companies.id],
  }),
  template: one(templates, {
    fields: [companyTemplates.templateId],
    references: [templates.id],
  }),
}));

export const templateRevisionRelations = relations(templateRevisions, ({ one, many }) => ({
  template: one(templates, {
    fields: [templateRevisions.templateId],
    references: [templates.id],
  }),
  fields: many(fields),
  agreements: many(agreements),
  publisher: one(users, {
    fields: [templateRevisions.publishedBy],
    references: [users.id],
  }),
}));

export const fields = pgTable('agreement_template_fields', {
  ...commonFieldsWithSoftDelete,
  name: varchar({ length: 255 }).notNull(),
  type: varchar({ length: 255 }).$type<FieldType>().notNull(),
  longForm: boolean().default(false).notNull(),
  valueOptions: text().array(),
  templateRevisionId: uuid()
    .references(() => templateRevisions.id)
    .notNull(),
});

export const values = pgTable(
  'agreement_template_field_values',
  {
    ...commonFieldsWithSoftDelete,
    fieldId: uuid()
      .references(() => fields.id, { onDelete: 'cascade' })
      .notNull(),
    valueString: varchar(),
    valueBoolean: boolean(),
    valueDate: timestamp(),
    valueIdentity: uuid(),
    companyId: uuid().references(() => companies.id),
    forSysAdmin: boolean().default(false).notNull(),
    agreementId: uuid().references(() => agreements.id),
  },
  table => [
    {
      checkConstraint: check(
        'value_field_not_null',
        sql`
      (
        ${table.valueString} IS NOT NULL
        OR ${table.valueBoolean} IS NOT NULL
        OR ${table.valueDate} IS NOT NULL
        OR ${table.valueIdentity} IS NOT NULL
      )
    `,
      ),
    },
  ],
);

export const valueRelations = relations(values, ({ one }) => ({
  field: one(fields, {
    fields: [values.fieldId],
    references: [fields.id],
  }),
  agreement: one(agreements, {
    fields: [values.agreementId],
    references: [agreements.id],
  }),
}));

export const fieldRelations = relations(fields, ({ one, many }) => ({
  values: many(values),
  templateRevision: one(templateRevisions, {
    fields: [fields.templateRevisionId],
    references: [templateRevisions.id],
  }),
}));

export const agreements = pgTable('agreements', {
  ...commonFieldsWithSoftDelete,
  companyId: uuid()
    .references(() => companies.id)
    .notNull(),
  templateRevisionId: uuid()
    .references(() => templateRevisions.id)
    .notNull(),
  name: varchar().notNull(),
});

export const agreementDocuments = pgTable('agreement_documents', {
  ...commonFieldsWithSoftDelete,
  agreementId: uuid()
    .references(() => agreements.id)
    .notNull(),
  fileId: uuid()
    .references(() => files.id)
    .notNull(),
});

export const agreementDocumentRelations = relations(agreementDocuments, ({ one }) => ({
  agreement: one(agreements, {
    fields: [agreementDocuments.agreementId],
    references: [agreements.id],
  }),
  file: one(files, {
    fields: [agreementDocuments.fileId],
    references: [files.id],
  }),
}));

export const agreementRelations = relations(agreements, ({ one, many }) => ({
  templateRevision: one(templateRevisions, {
    fields: [agreements.templateRevisionId],
    references: [templateRevisions.id],
  }),
  values: many(values),
  agreementDocuments: many(agreementDocuments),
}));
